import { PropsWithChildren, ReactNode, useState } from "react";
import { Link as RouterLink, useMatch, useNavigate, useParams } from "react-router-dom";
import { useCopyToClipboard } from "react-use";
import { KeyboardArrowRight, ExpandMore } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  buttonClasses,
  Divider,
  Menu,
  MenuItem,
  menuItemClasses,
  styled,
} from "@mui/material";
import { ButtonTypeMap } from "@mui/material/Button/Button";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import CreateFolderDialog from "files/components/CreateFolderDialog";
import DeleteFileDialog from "files/components/DeleteFileDialog";
import FileActionDialog from "files/components/FileActionDialog";
import FileTitle from "files/components/FileTitle";
import UploadFileDialog from "files/components/UploadFileDialog";
import useBasePath from "files/hooks/useBasePath";
import useFileAccess from "files/hooks/useFileAccess";
import {
  BASE_DIRECTORIES,
  encodeObjectKey,
  resolveObjectKey,
  resolveRouterPath,
  useCurrentPath,
} from "files/hooks/useFiles";
import BaseDirectory from "files/types/enums/baseDirectory";
import { JobType } from "files/types/interfaces/fileJob";
import { BooleanParam, useQueryParam } from "use-query-params";
import TextOverflow from "~/components/TextOverflow";
import useUser from "~/contexts/user";

export const BreadcrumbText = ({ children }: PropsWithChildren) => (
  <TextOverflow autoTooltip maxWidth={300} fontSize={20} fontWeight={600}>
    {children}
  </TextOverflow>
);

export const BreadcrumbButton = styled(Button)({
  padding: "0px 4px",
  margin: "0 -4px",
  [`& .${buttonClasses.endIcon}`]: {
    marginLeft: 4,
    svg: {
      marginTop: 2,
      fontSize: 18,
    },
  },
  "&:hover": {
    backgroundColor: "#E1E1E3",
  },
  "&:focus": {
    opacity: 1,
  },
}) as OverridableComponent<ButtonTypeMap>;

interface BreadCrumbProps {
  title: ReactNode;
  prefix: string;
}

export const MenuCrumb = ({ title, prefix }: BreadCrumbProps) => {
  const [, copy] = useCopyToClipboard();
  const basePath = useBasePath();
  const navigate = useNavigate();
  const canWrite = useFileAccess(prefix, "write");
  const isGlobalFileContext = !useMatch("/workbenches/:workbenchId/files/*");
  const routerPath = encodeObjectKey(resolveRouterPath(prefix, isGlobalFileContext));
  const canModify =
    canWrite &&
    !BASE_DIRECTORIES[routerPath] &&
    !new RegExp(`^${BaseDirectory.PROJECTS}/\\d+/?$`).test(routerPath);
  const [fileAction, setFileAction] = useState<JobType | null>(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleCopyLink = () => {
    const url = new URL(`${basePath}/${routerPath}`, window.location.origin);
    setAnchorEl(null);
    copy(url.toString());
  };

  const handleNewFolder = () => {
    setAnchorEl(null);
    setNewFolderOpen(true);
  };

  const handleCopyFolder = () => {
    setAnchorEl(null);
    setFileAction(JobType.COPY);
  };

  const handleMoveFolder = () => {
    setAnchorEl(null);
    setFileAction(JobType.MOVE);
  };

  const handleAddFiles = () => {
    setAnchorEl(null);
    setUploadDialogOpen(true);
  };

  const handleDelete = () => {
    setAnchorEl(null);
    setDeleteDialogOpen(true);
  };

  return (
    <>
      <BreadcrumbButton endIcon={<ExpandMore />} onClick={(e) => setAnchorEl(e.currentTarget)}>
        <BreadcrumbText>{title}</BreadcrumbText>
      </BreadcrumbButton>
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={(e: Event) => {
          e.stopPropagation();
          setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {canWrite && <MenuItem onClick={handleNewFolder}>New Folder</MenuItem>}
        {canWrite && <MenuItem onClick={handleAddFiles}>Add Files...</MenuItem>}
        {canWrite && <Divider />}
        <MenuItem onClick={handleCopyFolder}>Copy Folder...</MenuItem>
        {canModify && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setFileAction(JobType.RENAME);
            }}
          >
            Rename...
          </MenuItem>
        )}
        {canModify && <MenuItem onClick={handleMoveFolder}>Move...</MenuItem>}
        {canWrite && <Divider />}
        <MenuItem onClick={handleCopyLink}>Copy Link</MenuItem>
        {canModify && <Divider />}
        {canModify && (
          <MenuItem
            onClick={handleDelete}
            sx={{
              color: "attention.main",
              [`&.${menuItemClasses.root}:hover`]: {
                color: "common.white",
                bgcolor: "attention.main",
              },
            }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>
      <CreateFolderDialog open={newFolderOpen} onClose={() => setNewFolderOpen(false)} />
      <UploadFileDialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} />
      {fileAction && (
        <FileActionDialog
          isDirectory
          fileKey={prefix}
          open={!!fileAction}
          action={fileAction}
          handleClose={() => setFileAction(null)}
        />
      )}
      <DeleteFileDialog
        isDirectory
        fileKey={prefix.endsWith("/") ? prefix : `${prefix}/`}
        open={deleteDialogOpen}
        handleClose={(deleted) => {
          setDeleteDialogOpen(false);
          if (deleted) {
            navigate("..", { relative: "path" });
          }
        }}
      />
    </>
  );
};

const FileBreadcrumbs = () => {
  const user = useUser();
  const basePath = useBasePath();
  const filePath = useCurrentPath();
  const { projectId } = useParams();
  const [preview] = useQueryParam("preview", BooleanParam);
  const splitPath = filePath
    .split("/")
    .filter(String)
    .slice(0, preview ? -1 : undefined);

  const breadcrumbs = ["", ...splitPath].map((segment, index) => {
    const topDirectory = index === 0;
    const currentKey = resolveObjectKey(
      splitPath.slice(0, index).join("/"),
      user?.email,
      projectId
    );
    const routerPath = encodeObjectKey([basePath, ...splitPath.slice(0, index)].join("/"));
    const label = topDirectory ? "Files" : <FileTitle fileKey={currentKey} />;

    return {
      title: label,
      prefix: currentKey,
      href: routerPath,
    };
  });

  return (
    <Breadcrumbs separator={<KeyboardArrowRight sx={{ fontSize: 22 }} />}>
      {breadcrumbs.map((crumb, index) => {
        if (breadcrumbs.length === 1) {
          return (
            <Box key={crumb.href} display="flex" alignItems="center" minHeight={31.5}>
              <BreadcrumbText>{crumb.title}</BreadcrumbText>
            </Box>
          );
        }

        if (index === breadcrumbs.length - 1) {
          return <MenuCrumb key={crumb.href} prefix={crumb.prefix} title={crumb.title} />;
        }

        return (
          <BreadcrumbButton key={crumb.href} component={RouterLink} to={crumb.href}>
            <BreadcrumbText>{crumb.title}</BreadcrumbText>
          </BreadcrumbButton>
        );
      })}
    </Breadcrumbs>
  );
};

export default FileBreadcrumbs;
